import React, { useState, useEffect } from 'react';
import './BatchPaymentModal.css';

const BatchPaymentModal = ({ 
  isOpen, 
  onClose, 
  selectedCupls, 
  onProcessPayment, 
  balance,
  onComplete,
  title = "Procesamiento por Lote",
  actionLabel = "Iniciar Procesamiento"
}) => {
  const [currentStep, setCurrentStep] = useState(0); // Index of the CUPL being processed
  const [status, setStatus] = useState('idle'); // idle, processing, completed, error
  const [results, setResults] = useState([]); // Array of { id, cupl_number, status, message }
  const [isPaused, setIsPaused] = useState(false);

  const totalAmount = selectedCupls.reduce((sum, cupl) => sum + (parseFloat(cupl.total_amount || cupl.tramite_value) || 0), 0);
  const hasEnoughBalance = balance >= totalAmount;

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setStatus('idle');
      setResults([]);
      setIsPaused(false);
    }
  }, [isOpen]);

  const startBatch = async () => {
    if (!hasEnoughBalance) {
      alert('No hay saldo suficiente para procesar todos los registros seleccionados.');
      return;
    }
    
    setStatus('processing');
    setResults([]);
    
    for (let i = 0; i < selectedCupls.length; i++) {
      if (isPaused) break;
      
      const cupl = selectedCupls[i];
      setCurrentStep(i);
      
      try {
        // Validación extra antes de cada paso
        if (cupl.is_paid) {
          throw new Error('Este CUPL ya figura como pagado');
        }

        const result = await onProcessPayment(cupl);
        setResults(prev => [...prev, { 
          id: cupl.id, 
          cupl_number: cupl.cupl_number, 
          status: 'success', 
          message: typeof result === 'string' ? result : (result?.mensaje || 'Exitoso') 
        }]);
      } catch (err) {
        const errorMsg = err.response?.data?.mensaje || err.response?.data?.message || err.message || 'Error en el proceso';
        setResults(prev => [...prev, { 
          id: cupl.id, 
          cupl_number: cupl.cupl_number, 
          status: 'error', 
          message: errorMsg 
        }]);
        setStatus('error');
        // El bucle se corta aquí (return)
        onComplete(); // Refrescar para ver qué se alcanzó a pagar
        return;
      }
    }
    
    setStatus('completed');
    onComplete();
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content batch-payment-modal">
        <div className="modal-header">
          <h2>📦 {title}</h2>
          <button className="close-btn" onClick={onClose} disabled={status === 'processing'}>&times;</button>
        </div>
        
        <div className="batch-summary">
          <div className="summary-item">
            <span>Items seleccionados:</span>
            <strong>{selectedCupls.length}</strong>
          </div>
          <div className="summary-item">
            <span>Total a descontar:</span>
            <strong className="amount">${totalAmount.toLocaleString('es-CO')}</strong>
          </div>
          <div className="summary-item">
            <span>Saldo disponible:</span>
            <strong className={hasEnoughBalance ? 'balance-ok' : 'balance-error'}>
              ${balance?.toLocaleString('es-CO')}
            </strong>
          </div>
        </div>

        {status === 'idle' && (
          <div className="batch-actions">
            <p className="instruction">
              Se procesarán {selectedCupls.length} registros uno por uno automáticamente. 
              <strong> El proceso se detendrá si ocurre algún error.</strong>
            </p>
            <button 
              className="btn-primary start-batch-btn" 
              onClick={startBatch}
              disabled={!hasEnoughBalance || selectedCupls.length === 0}
            >
              {actionLabel}
            </button>
          </div>
        )}

        {(status === 'processing' || status === 'completed' || status === 'error') && (
          <div className="batch-progress">
            <div className="progress-bar-container">
              <div 
                className={`progress-bar ${status === 'error' ? 'error' : status === 'completed' ? 'success' : ''}`} 
                style={{ width: `${((results.length) / selectedCupls.length) * 100}%` }}
              ></div>
            </div>
            
            <div className="progress-stats">
              <span className="stat-success">Exitosos: {successCount}</span>
              <span className="stat-error">Errores: {errorCount}</span>
              <span className="stat-total">Total: {results.length} / {selectedCupls.length}</span>
            </div>

            <p className="progress-text">
              {status === 'processing' ? `Procesando ${results.length + 1} de ${selectedCupls.length}...` : 
               status === 'completed' ? '✅ Proceso finalizado exitosamente' : '❌ Proceso detenido por error'}
            </p>

            <div className="results-list">
              {results.map((res, idx) => (
                <div key={idx} className={`result-item ${res.status}`}>
                  <span className="res-cupl">{res.cupl_number}</span>
                  <span className="res-status">{res.status === 'success' ? '✅' : '❌'}</span>
                  <span className="res-msg">{res.message}</span>
                </div>
              ))}
              {status === 'processing' && (
                <div className="result-item processing">
                  <span className="res-cupl">{selectedCupls[currentStep]?.cupl_number}</span>
                  <span className="res-status"><div className="spinner-mini"></div></span>
                  <span className="res-msg">Procesando...</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={status === 'processing'}
          >
            {status === 'completed' || status === 'error' ? 'Cerrar y Ver Resultados' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchPaymentModal;
